import { useCallback, useEffect, useState, type ReactNode } from "react"
import { useLocation } from "@tanstack/react-router"

import {
  SIDEBAR_PREFERENCE,
  SIDEBAR_STORAGE_KEY,
  activeNavigationItem,
  isSidebarShortcut,
  readSidebarPreference,
} from "./app-navigation"
import { DesktopSidebar, MobileAppHeader, MobileSidebarDrawer } from "./app-sidebar"
import { GlobalChat } from "../chat/global-chat"
import { cn } from "../../lib/utils"
import { isSyncing, useOrderSyncProgress } from "../../services/order-sync"

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)"

export function AuthenticatedAppLayout({ children }: { children: ReactNode }) {
  const pathname = useLocation({ select: (location) => location.pathname })
  const [isDesktopExpanded, setDesktopExpanded] = useState(readStoredPreference)
  const [isDesktop, setDesktop] = useState(readDesktopViewport)
  const [isMobileOpen, setMobileOpen] = useState(false)
  const isMac = isMacPlatform()
  const shortcutLabel = isMac ? "⌘B" : "Ctrl+B"
  const activeItem = activeNavigationItem(pathname)
  const isSyncingOrders = isSyncing(useOrderSyncProgress())

  const toggleDesktop = useCallback(() => {
    setDesktopExpanded((isExpanded) => !isExpanded)
  }, [])

  useEffect(() => {
    try {
      globalThis.localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        isDesktopExpanded ? SIDEBAR_PREFERENCE.expanded : SIDEBAR_PREFERENCE.collapsed
      )
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }, [isDesktopExpanded])

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia(DESKTOP_MEDIA_QUERY)
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setDesktop(event.matches)
      if (event.matches) {
        setMobileOpen(false)
      }
    }

    setDesktop(mediaQuery.matches)
    if (mediaQuery.matches) {
      setMobileOpen(false)
    }
    mediaQuery.addEventListener("change", handleBreakpointChange)
    return () => mediaQuery.removeEventListener("change", handleBreakpointChange)
  }, [])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!isSidebarShortcut(event, isMac)) {
        return
      }
      event.preventDefault()
      if (isDesktop) {
        toggleDesktop()
      } else {
        setMobileOpen((isOpen) => !isOpen)
      }
    }
    globalThis.document.addEventListener("keydown", handleShortcut)
    return () => globalThis.document.removeEventListener("keydown", handleShortcut)
  }, [isDesktop, isMac, toggleDesktop])

  return (
    <div className="min-h-screen bg-background">
      {isDesktop ? (
        <DesktopSidebar
          activeItem={activeItem}
          isExpanded={isDesktopExpanded}
          isSyncingOrders={isSyncingOrders}
          onToggle={toggleDesktop}
          shortcutLabel={shortcutLabel}
        />
      ) : (
        <MobileAppHeader
          isOpen={isMobileOpen}
          onOpen={() => setMobileOpen(true)}
          shortcutLabel={shortcutLabel}
        />
      )}

      <div
        className={cn(
          "min-w-0 transition-[padding-left] duration-200 ease-[var(--ease-product-out)] motion-reduce:transition-none",
          isDesktop && (isDesktopExpanded ? "pl-60" : "pl-16")
        )}
      >
        {children}
      </div>

      <GlobalChat />

      {!isDesktop ? (
        <MobileSidebarDrawer
          activeItem={activeItem}
          isOpen={isMobileOpen}
          isSyncingOrders={isSyncingOrders}
          onClose={() => setMobileOpen(false)}
          shortcutLabel={shortcutLabel}
        />
      ) : null}
    </div>
  )
}

function readStoredPreference(): boolean {
  try {
    return readSidebarPreference(globalThis.localStorage.getItem(SIDEBAR_STORAGE_KEY))
  } catch {
    return true
  }
}

function isMacPlatform(): boolean {
  return /Mac|iPhone|iPad/.test(globalThis.navigator.platform)
}

function readDesktopViewport(): boolean {
  return globalThis.matchMedia(DESKTOP_MEDIA_QUERY).matches
}
